import { useEffect } from "react";

import { SpinnerCenter } from "@shapr3d/frontend-lib";
import { useLocation, useParams } from "react-router-dom";

import ErrorPage from "../components/ErrorPage/ErrorPage";
import PasswordResetInput from "../components/PasswordResetInput/PasswordResetInput";
import useNavigation from "../hooks/navigation";
import { useCheckResetPasswordHash, useResetPassword } from "../hooks/useAuth";
import { useTracker } from "../hooks/useTracker";
import useTranslation from "../hooks/useTranslation";
import { AuthError, SearchParam } from "../types";

const PasswordResetInputContainer: React.FC = () => {
  const navigate = useNavigation();
  const tracker = useTracker();
  const location = useLocation();
  const { t, m } = useTranslation();

  // We support both URL param and URL hash for backward compatibility
  const hashParams = new URLSearchParams(location.hash?.replace("#", "?"));
  const confirmationHashUrlHash = hashParams.get(SearchParam.Hash);

  const { confirmationHash: confirmationHashParam } = useParams();
  const confirmationHash = confirmationHashUrlHash || confirmationHashParam;

  const { isLoading: isCheckResetPasswordHashLoading, isError: isCheckResetPasswordHashError } =
    useCheckResetPasswordHash({ confirmationHash });

  const {
    mutate: mutateResetPassword,
    isPending: isResetPasswordPending,
    isSuccess: isResetPasswordSuccess,
    isError: isResetPasswordError,
    error: resetPasswordError,
  } = useResetPassword({ confirmationHash });

  useEffect(() => {
    if (isCheckResetPasswordHashError) {
      navigate.goToPasswordResetHashError();
    }
  }, [isCheckResetPasswordHashError, navigate, tracker]);

  useEffect(() => {
    if (isResetPasswordSuccess) {
      navigate.goToPasswordResetSuccess();
    }
  });

  if (!confirmationHash) {
    return <ErrorPage />;
  }

  const submit = async (password: string) => {
    mutateResetPassword({ password });
  };

  if (isCheckResetPasswordHashLoading) {
    return <SpinnerCenter />;
  }

  if (isResetPasswordError && resetPasswordError !== AuthError.PasswordUnchanged) {
    return <ErrorPage />;
  }
  return (
    <PasswordResetInput
      errorMessage={
        resetPasswordError === AuthError.PasswordUnchanged
          ? t(m.ErrorMessage.PasswordUnchanged)
          : undefined
      }
      onSubmit={submit}
      inProgress={isResetPasswordPending}
    />
  );
};

export default PasswordResetInputContainer;
